import React from 'react'
import { useTranslation } from 'react-i18next'
import { usePageConfigUser, useThirdPartySettings } from 'hooks/usePageConfig'
import BubbleChart, { SeriesData } from 'components/visualization/Bubble'
import { getYearDateDisplay, getYearDateHourMinuteDisplay } from 'utils/timeZoneUtil'
import BubbleDisplayOptions, { BubbleDisplayOptionsStrings } from 'enums/BubbleDisplayOptions'
import TimeInterval, { TimeIntervalStrings } from 'enums/TimeInterval'
import messageUtil from 'utils/messageUtil'
import MetricSeries from 'enums/MetricSeries'
import { TimeZoneStrings } from 'enums/TimeZone'
import resolveMinRadiusInPercentage from './utils/minRadiusResolver'
import { INSUFFICIENT_BOUND, LOW_DATA_BOUND } from './utils/constants'
import { BubbleData } from '../types/bubbleData'

const MAX_SIZE_IN_PERCENTAGE = '54%'

const isGifImage = (fileName: string) => fileName.trim().toLowerCase().endsWith('.gif')

const resolveImageUrl = (
  image: string,
  displayOption: BubbleDisplayOptionsStrings,
  s3ImagePrepend: string
) => {
  if (displayOption !== BubbleDisplayOptions.IMAGES || image.length === 0) {
    return undefined
  }

  if (image.indexOf('@') === 0) {
    return `url(https://twitter.com/${image}/profile_image?size=bigger)`
  }

  if (isGifImage(image)) {
    return undefined
  }

  if (image.startsWith('*')) {
    return `url(${s3ImagePrepend}${image.substring(1)})`
  }

  return `url(${image})`
}

const resolveSeriesData = (
  bubble: BubbleData,
  timeZone: TimeZoneStrings,
  timeInterval: TimeIntervalStrings
) =>
  bubble.series.map((series) => {
    const { x, y, size, noData, supported, startDate, endDate } = series
    if (noData || !supported) {
      return { insufficient: true }
    }

    let tooltipSubtitle: string

    switch (timeInterval) {
      case TimeInterval.LAST_HOUR:
      case TimeInterval.OPEN_END:
      case TimeInterval.CUSTOM:
      case TimeInterval.ALL:
        tooltipSubtitle = `${getYearDateHourMinuteDisplay(
          startDate,
          timeZone
        )} - ${getYearDateHourMinuteDisplay(endDate, timeZone)}`
        break
      default:
        tooltipSubtitle = `${getYearDateDisplay(startDate, timeZone)} - ${getYearDateDisplay(
          endDate,
          timeZone
        )}`
    }

    return {
      x,
      y,
      z: size,
      lowData: size < LOW_DATA_BOUND,
      insufficient: size < INSUFFICIENT_BOUND,
      tooltipSubtitle
    }
  })

type BubbleProps = {
  bubbleData: BubbleData[]
  displayOption: BubbleDisplayOptionsStrings
  timeInterval: TimeIntervalStrings
}

export const Bubble = ({ bubbleData, displayOption, timeInterval }: BubbleProps) => {
  const { timeZone } = usePageConfigUser()
  const { s3ImagePrepend } = useThirdPartySettings()
  const { t } = useTranslation()

  const chartData = bubbleData!.map((bubble) => {
    const { name, image } = bubble
    const allInsufficient = bubble.series.every((series) => series.size < INSUFFICIENT_BOUND)
    const allNoData = bubble.series.every((series) => series.noData)
    const languageSupported = bubble.series.every((series) => series.supported)
    const url = resolveImageUrl(image, displayOption, s3ImagePrepend)

    let seriesData: SeriesData[] = []
    if (languageSupported && !allInsufficient) {
      seriesData = resolveSeriesData(bubble, timeZone, timeInterval)
    }

    let tooltipText: string | undefined
    if (!languageSupported) {
      tooltipText = t('topicLanguageNotSupported')
    } else if (allNoData) {
      tooltipText = t('cannotDisplayNoDataTopic')
    } else if (allInsufficient) {
      tooltipText = t('cannotDisplayLowDataTopic', { threshold: INSUFFICIENT_BOUND })
    }

    return {
      name,
      data: seriesData,
      ...(url && { marker: { symbol: url } }),
      ...(tooltipText && {
        custom: {
          legendTooltip: {
            enable: true,
            text: tooltipText
          }
        }
      })
    }
  })

  const minRadiusInPercentage = resolveMinRadiusInPercentage(bubbleData)
  return (
    <BubbleChart
      data={chartData}
      xAxisTitle={messageUtil.measureType(MetricSeries.NET_SENTIMENT)}
      yAxisTitle={messageUtil.measureType(MetricSeries.PASSION_ON_UNIQUE_AUTHORS)}
      zAxisTitle={messageUtil.measureType(MetricSeries.TOTAL_BUZZ)}
      insufficientThreshold={INSUFFICIENT_BOUND}
      lowDataThreshold={LOW_DATA_BOUND}
      lowDataTooltipTitlePostfix={`(${t('lowDataVolume')})`}
      lowDataTooltipDescription={t('bubbleLowDataTooltip')}
      quadrantLabels={[t('love'), t('hate'), t('dislike'), t('like')]}
      xAxisLabelFormatter={({ value }: { value: number }) =>
        value > 100 || value < -100 ? '' : value
      }
      yAxisLabelFormatter={({ value }: { value: number }) =>
        value > 100 || value < 0 ? '' : value
      }
      maxSize={MAX_SIZE_IN_PERCENTAGE}
      minSize={`${minRadiusInPercentage}%`}
    />
  )
}

export default Bubble
